import React, { useState, useEffect, useCallback } from 'react'; 
import { useNavigate } from 'react-router-dom';// Import the AdminSidebar component
import AdminSidebar from './Components/AdminSidebar';
// Icon set now includes FaChevronLeft and FaChevronRight
import { FaSearch, FaEdit, FaTrashAlt, FaCalendarAlt, FaChevronDown, FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight, FaCheck, FaDownload } from 'react-icons/fa';
import { HiDocumentText } from 'react-icons/hi';
import { MdVisibility } from 'react-icons/md';
import { LineChart } from '@mui/x-charts/LineChart';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const DEFAULT_PAGE_LIMIT = 10;
const ADMIN_TOKEN_KEY = 'ADMIN_TOKEN';

const SERVICE_INTEREST_LABELS = {
    'administrative-support': 'Administrative Support',
    'legal-compliance-support': 'Legal & Compliance Support',
    'project-management-support': 'Project Management Support',
    'client-relations-communication': 'Client Relations & Communication',
    'financial-planning': 'Financial Planning',
    'quantity-surveying-estimating': 'Quantity Surveying & Estimating',
    'procurement-vendor-management': 'Procurement & Vendor Management',
    'social-media-content-management': 'Social Media & Content Management',
};

const STATUS_DISPLAY_MAP = {
    new: 'Pending',
    pending: 'Pending',
    contacted: 'Processing',
    processing: 'Processing',
    closed: 'Completed',
    completed: 'Completed',
};

const formatServiceInterest = (value) => SERVICE_INTEREST_LABELS[value] || 'Not specified';

const formatStatus = (value) => {
    if (!value) return 'Pending';
    return STATUS_DISPLAY_MAP[value.toLowerCase()] || value;
};

const STATUS_DISPLAY_OPTIONS = ['Pending', 'Processing', 'Completed'];
const STATUS_DISPLAY_TO_API = {
    Pending: 'new',
    Processing: 'contacted',
    Completed: 'closed',
};

const formatDate = (value) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const buildPageNumbers = (current, total) => {
    if (total <= 7) {
        return Array.from({ length: total }, (_, idx) => idx + 1);
    }

    const pages = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    if (start > 2) {
        pages.push('...');
    }

    for (let i = start; i <= end; i += 1) {
        pages.push(i);
    }

    if (end < total - 1) {
        pages.push('...');
    }

    pages.push(total);
    return pages;
};


const AdminDashboard = () => {
  const navigate = useNavigate();
  // ... (State declarations: isSidebarCollapsed, inquiries, getStatusBadge, pageNumbers) ...
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false); 
  const [selectedRows, setSelectedRows] = useState([]);
  const [inquiriesData, setInquiriesData] = useState([]);
  const [authToken, setAuthToken] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY) || '');
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 1, total: 0, limit: DEFAULT_PAGE_LIMIT });
  const [reloadKey, setReloadKey] = useState(0);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [actionBanner, setActionBanner] = useState(null);
  const [viewingInquiry, setViewingInquiry] = useState(null);
  const [selectedQueryType, setSelectedQueryType] = useState(null); // Filter by query type
  const [dateSortOrder, setDateSortOrder] = useState(null); // 'asc' or 'desc' for date sorting
  const [dateFilterStart, setDateFilterStart] = useState(null); // Start date for filtering
  const [dateFilterEnd, setDateFilterEnd] = useState(null); // End date for filtering
  const [showQueryTypeFilter, setShowQueryTypeFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState(''); // Search query for filtering
  
  // --- New state for the active sidebar link ---
  const [activeLink, setActiveLink] = useState('Dashboard'); // Default is 'Dashboard'

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAuthToken('');
    navigate('/adminlogin');
  };

  // Handle navigation from sidebar
  const handleNavigation = (linkName, route) => {
    setActiveLink(linkName);
    navigate(route);
  };

  // Dynamic widths
  const fullWidth = '360px';
  const collapsedWidth = '140px'; 
  
const fallbackTrend = [
    { month: 'Jan', inquiries: 0, resolved: 0 },
    { month: 'Feb', inquiries: 0, resolved: 0 },
    { month: 'Mar', inquiries: 0, resolved: 0 },
    { month: 'Apr', inquiries: 0, resolved: 0 },
    { month: 'May', inquiries: 0, resolved: 0 },
    { month: 'Jun', inquiries: 0, resolved: 0 },
    { month: 'Jul', inquiries: 0, resolved: 0 },
    { month: 'Aug', inquiries: 0, resolved: 0 },
    { month: 'Sep', inquiries: 0, resolved: 0 },
    { month: 'Oct', inquiries: 0, resolved: 0 },
    { month: 'Nov', inquiries: 0, resolved: 0 },
    { month: 'Dec', inquiries: 0, resolved: 0 },
  ];

  const [trendData, setTrendData] = useState([]);
  const [isTrendLoading, setIsTrendLoading] = useState(true);
  const [trendError, setTrendError] = useState(null);

  useEffect(() => {
    if (!authToken) {
      navigate('/adminlogin');
    }
  }, [authToken, navigate]);

  // --- Updated getStatusBadge helper ---
  const getStatusBadge = (status) => {
    let bgColor, textColor;
    
    // Colors aligned with the mockup (light backgrounds)
    switch (status) {
      case 'Completed':
        bgColor = 'bg-green-500/20';
        textColor = 'text-green-300';
        break;
      case 'Processing':
        bgColor = 'bg-orange-400/20';
        textColor = 'text-orange-200';
        break;
      case 'Pending':
        bgColor = 'bg-red-500/20';
        textColor = 'text-red-300';
        break;
      default:
        bgColor = 'bg-gray-500/20';
        textColor = 'text-gray-200';
        break;
    }
    return (
      // Includes 'inline-block w-28 text-center' to keep the badge width consistent
      <span className={`inline-block w-28 text-center px-3 py-1 rounded-md text-xs font-semibold ${bgColor} ${textColor}`}>
        {status}
      </span>
    );
  };
  // --- End of helper update ---

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const toggleRowSelection = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const allRowsSelected = selectedRows.length === inquiriesData.length && inquiriesData.length > 0;

  const toggleSelectAll = () => {
    if (allRowsSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(inquiriesData.map((inq) => inq._id));
    }
  };

  const statusOptions = STATUS_DISPLAY_OPTIONS;
  const getAuthHeaders = useCallback((headers = {}) => {
    if (!authToken) return headers;
    return {
      ...headers,
      Authorization: `Bearer ${authToken}`,
    };
  }, [authToken]);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAuthToken('');
    navigate('/adminlogin');
  }, [navigate]);

  useEffect(() => {
    if (!authToken) return;
    let isMounted = true;

    const fetchInquiries = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/lets-connect?page=${page}&limit=${DEFAULT_PAGE_LIMIT}`, {
          headers: getAuthHeaders(),
        });
        if (response.status === 401) {
          handleUnauthorized();
          return;
        }
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || 'Failed to load inquiries');
        }

        if (isMounted) {
          let inquiries = result.data || [];
          
          // Apply search query filter
          if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            inquiries = inquiries.filter(inq => {
              const name = (inq.fullName || '').toLowerCase();
              const email = (inq.email || '').toLowerCase();
              const serviceInterest = formatServiceInterest(inq.serviceInterest).toLowerCase();
              const description = (inq.description || '').toLowerCase();
              const status = formatStatus(inq.status).toLowerCase();
              
              return name.includes(query) || 
                     email.includes(query) || 
                     serviceInterest.includes(query) ||
                     description.includes(query) ||
                     status.includes(query);
            });
          }
          
          // Apply query type filter
          if (selectedQueryType) {
            inquiries = inquiries.filter(inq => inq.serviceInterest === selectedQueryType);
          }
          
          // Apply date range filter
          if (dateFilterStart || dateFilterEnd) {
            inquiries = inquiries.filter(inq => {
              const inquiryDate = new Date(inq.createdAt);
              inquiryDate.setHours(0, 0, 0, 0);
              
              if (dateFilterStart && dateFilterEnd) {
                const start = new Date(dateFilterStart);
                start.setHours(0, 0, 0, 0);
                const end = new Date(dateFilterEnd);
                end.setHours(23, 59, 59, 999);
                return inquiryDate >= start && inquiryDate <= end;
              } else if (dateFilterStart) {
                const start = new Date(dateFilterStart);
                start.setHours(0, 0, 0, 0);
                return inquiryDate >= start;
              } else if (dateFilterEnd) {
                const end = new Date(dateFilterEnd);
                end.setHours(23, 59, 59, 999);
                return inquiryDate <= end;
              }
              return true;
            });
          }
          
          // Apply date sorting
          if (dateSortOrder) {
            inquiries = [...inquiries].sort((a, b) => {
              const dateA = new Date(a.createdAt);
              const dateB = new Date(b.createdAt);
              return dateSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
            });
          } else {
            // Default: newest first
            inquiries = [...inquiries].sort((a, b) => {
              const dateA = new Date(a.createdAt);
              const dateB = new Date(b.createdAt);
              return dateB - dateA;
            });
          }
          
          setInquiriesData(inquiries);
          setPagination({
            page: result.pagination?.page || page,
            totalPages: result.pagination?.totalPages || 1,
            total: inquiries.length,
            limit: result.pagination?.limit || DEFAULT_PAGE_LIMIT,
          });
          setFetchError(null);
          setSelectedRows([]);
        }
      } catch (error) {
        if (isMounted) {
          setFetchError(error.message || 'Failed to load inquiries');
          setInquiriesData([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchInquiries();

    return () => {
      isMounted = false;
    };
  }, [page, reloadKey, authToken, selectedQueryType, dateSortOrder, dateFilterStart, dateFilterEnd, searchQuery, getAuthHeaders, handleUnauthorized]);

  useEffect(() => {
    if (!authToken) return;
    let isMounted = true;

    const fetchTrend = async () => {
      setIsTrendLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/lets-connect/stats/overview?months=12`, {
          headers: getAuthHeaders(),
        });
        if (response.status === 401) {
          handleUnauthorized();
          return;
        }
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.message || 'Failed to load chart data');
        }
        if (isMounted) {
          const data = (result.months || []).map((entry) => ({
            month: entry.month,
            inquiries: entry.inquiries ?? entry.created ?? 0,
            resolved: entry.resolved,
          }));
          setTrendData(data);
          setTrendError(null);
        }
      } catch (error) {
        if (isMounted) {
          setTrendError(error.message || 'Failed to load chart data');
          setTrendData([]);
        }
      } finally {
        if (isMounted) {
          setIsTrendLoading(false);
        }
      }
    };

    fetchTrend();

    return () => {
      isMounted = false;
    };
  }, [authToken, reloadKey, getAuthHeaders, handleUnauthorized]);

  useEffect(() => {
    if (!actionBanner) return undefined;
    const timer = setTimeout(() => setActionBanner(null), 4000);
    return () => clearTimeout(timer);
  }, [actionBanner]);

  const totalPages = pagination.totalPages || 1;
  const pageNumbers = buildPageNumbers(page, totalPages);
  const hasPrev = page > 1;
  const hasNext = page < totalPages;
  const hasFirst = hasPrev;
  const hasLast = hasNext;
  const chartDataset = trendData.length ? trendData : fallbackTrend;
  const isChartEmpty = !trendData.length && !isTrendLoading && !trendError;

  const handleStatusEditToggle = (id) => {
    setEditingStatusId((prev) => (prev === id ? null : id));
  };

  const showActionMessage = (type, message) => {
    setActionBanner({ type, message, timestamp: Date.now() });
  };

  const handleStatusChange = async (id, displayStatus) => {
    const apiStatus = STATUS_DISPLAY_TO_API[displayStatus] || 'new';
    setStatusUpdatingId(id);
    try {
      const response = await fetch(`${API_BASE_URL}/lets-connect/${id}`, {
        method: 'PATCH',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ status: apiStatus }),
      });
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || 'Failed to update status');
      }
      showActionMessage('success', 'Status updated successfully.');
      setEditingStatusId(null);
      setReloadKey((key) => key + 1);
    } catch (error) {
      showActionMessage('error', error.message || 'Failed to update status.');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleDeleteInquiry = async (id) => {
    const shouldDelete = window.confirm('Remove this inquiry from the table? It will remain archived in the database.');
    if (!shouldDelete) return;

    setDeletingId(id);
    try {
      const response = await fetch(`${API_BASE_URL}/lets-connect/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || 'Failed to delete inquiry');
      }
      showActionMessage('success', 'Inquiry archived.');
      setReloadKey((key) => key + 1);
    } catch (error) {
      showActionMessage('error', error.message || 'Failed to delete inquiry.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownloadPdf = async (id) => {
    setDownloadingId(id);
    try {
      const response = await fetch(`${API_BASE_URL}/lets-connect/${id}/pdf`, {
        headers: getAuthHeaders(),
      });
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.message || 'Failed to download PDF');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `lets-connect-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showActionMessage('error', error.message || 'Failed to download PDF.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleBulkDownload = async () => {
    if (selectedRows.length === 0) {
      showActionMessage('error', 'Please select at least one inquiry to download.');
      return;
    }

    setIsBulkDownloading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/lets-connect/bulk-download`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ ids: selectedRows }),
      });
      
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      
      // Check content type to determine if it's an error JSON or a ZIP file
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok) {
        // Try to parse as JSON error, but handle case where it might be a blob
        if (contentType.includes('application/json')) {
          const result = await response.json().catch(() => ({}));
          throw new Error(result.message || 'Failed to download ZIP file');
        } else {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
      }
      
      // Verify it's actually a ZIP file
      if (!contentType.includes('application/zip') && !contentType.includes('application/octet-stream')) {
        // Might be an error response that wasn't caught
        const text = await response.text();
        try {
          const result = JSON.parse(text);
          throw new Error(result.message || 'Failed to download ZIP file');
        } catch {
          throw new Error('Unexpected response format from server');
        }
      }
      
      const blob = await response.blob();
      
      // Verify blob is not empty and has reasonable size
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty. No PDFs were generated.');
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `inquiries-${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showActionMessage('success', `Successfully downloaded ${selectedRows.length} inquiry PDFs.`);
    } catch (error) {
      showActionMessage('error', error.message || 'Failed to download ZIP file.');
    } finally {
      setIsBulkDownloading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      showActionMessage('error', 'Please select at least one inquiry to remove.');
      return;
    }
    const shouldDelete = window.confirm(
      `Remove ${selectedRows.length} selected inquiry/inquiries from the table? They will remain archived in the database.`
    );
    if (!shouldDelete) return;

    setIsBulkDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/lets-connect/bulk-delete`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ ids: selectedRows }),
      });
      if (response.status === 401) {
        handleUnauthorized();
        return;
      }
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || 'Failed to remove inquiries.');
      }
      showActionMessage('success', result.archivedCount ? `Archived ${result.archivedCount} inquiry/inquiries.` : 'Selected inquiries archived.');
      setSelectedRows([]);
      setReloadKey((key) => key + 1);
    } catch (error) {
      showActionMessage('error', error.message || 'Failed to remove inquiries.');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleViewInquiry = (inquiry) => {
    setViewingInquiry(inquiry);
  };

  const closeViewModal = () => setViewingInquiry(null);

  const handlePageChange = (targetPage) => {
    if (targetPage < 1 || targetPage > totalPages || targetPage === page) return;
    setPage(targetPage);
  };

  const handleRetryFetch = () => setReloadKey((key) => key + 1);
  
  return (
    <>
    {/* Updated: switched to 'h-screen' and added 'overflow-hidden' */}
    <div 
        className="h-screen bg-[#151419] overflow-hidden" 
        style={{
            display: 'grid',
            // Dynamic gridTemplateColumns adjusts based on sidebar state
            gridTemplateColumns: isSidebarCollapsed ? `${collapsedWidth} 1fr` : `${fullWidth} 1fr`,
            // Added smooth transition for the grid
            transition: 'grid-template-columns 300ms ease-in-out'
        }}
    >
      
      {/* 1. Sidebar: receives collapse state, toggle handler, and active link */}
      <AdminSidebar 
        isCollapsed={isSidebarCollapsed} 
        onToggle={toggleSidebar} 
        activeLink={activeLink}
        setActiveLink={setActiveLink}
        onLogout={handleLogout}
        onNavigate={handleNavigation}
      />

      {/* 2. Main Content Area */}
      {/* Updated: scrolling is limited to this container via 'overflow-y-auto' */}
      <div className="p-8 overflow-y-auto overflow-x-auto"> 
        
        {/* Top Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold select-none cursor-default text-[#FFFFFF]">Dashboard</h1>
        </div>

        {/* Line Chart Card */}
        <div className="bg-[#1D1C21] border border-white/10 rounded-2xl p-6 shadow-[0_25px_80px_rgba(0,0,0,0.45)] mb-8 select-none cursor-default">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
            <div>
              <h2 className="text-2xl font-semibold text-white select-none cursor-default">Dashboard Overview</h2>
              <p className="text-sm text-gray-400 select-none cursor-default">Monthly inquiries vs. resolved cases</p>
            </div>
            <div className="flex items-center gap-3 text-sm text-white select-none cursor-default">
              <span className="flex items-center gap-2 text-white select-none cursor-default">
                <span className="inline-block w-3 h-3 rounded-full bg-[#E68A40]"></span>
                <span className="text-white">Inquiries</span>
              </span>
              <span className="flex items-center gap-2 text-white">
                <span className="inline-block w-3 h-3 rounded-full bg-[#5BE4A4]"></span>
                <span className="text-white">Resolved</span>
              </span>
            </div>
          </div>
          {trendError && (
            <p className="text-sm text-red-400 mb-4">{trendError}</p>
          )}
          {isChartEmpty && (
            <p className="text-sm text-gray-400 mb-4">No inquiries recorded for this period yet.</p>
          )}
          <LineChart
            dataset={chartDataset}
            xAxis={[{
              dataKey: 'month',
              scaleType: 'point',
              tickLabelStyle: { fill: '#9EA0B7', fontSize: 12 },
              labelStyle: { fill: '#9EA0B7' },
            }]}
            yAxis={[{
              tickLabelStyle: { fill: '#9EA0B7', fontSize: 12 },
              gridLineStyle: { stroke: '#2F2E35' },
            }]}
            series={[
              { dataKey: 'inquiries', label: 'Inquiries', area: true, curve: 'monotoneX', color: '#E68A40', showMark: false },
              { dataKey: 'resolved', label: 'Resolved', area: true, curve: 'monotoneX', color: '#5BE4A4', showMark: false },
            ]}
            height={320}
            margin={{ left: 0, right: 20, top: 20, bottom: 20 }}
            slotProps={{
              legend: {
                position: { vertical: 'top', horizontal: 'right' },
                labelStyle: { color: '#FFFFFF', fill: '#FFFFFF', fontSize: 12 },
              },
            }}
            sx={{
              '& .MuiLineElement-root': { strokeWidth: 3 },
              '& .MuiAreaElement-root': { opacity: 0.12 },
              '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: '#2F2E35' },
              '& .MuiChartsAxisHighlight-root line': { stroke: '#E68A40' },
              '& .MuiChartsLegend-root': { color: '#FFFFFF' }, 
              '& .MuiChartsLegend-series text': { fill: '#FFFFFF' },
              '& .MuiChartsLegend-label': { color: '#FFFFFF', fill: '#FFFFFF' },
            }} 
          /> {/* --- This code edits the color inside the line graph (legends, lines etc)--- */}
          {isTrendLoading && (
            <p className="text-sm text-gray-400 mt-4">Loading chart...</p>
          )}
        </div>

        {/* --- TABLE CONTAINER --- */}
        <div
          className="bg-[#1D1C21] rounded-2xl shadow-[0_25px_80px_rgba(0,0,0,0.45)] overflow-x-auto border border-white/10 select-none cursor-default"
          style={{ userSelect: 'none' }}
        > 
          
          {/* --- TOP FILTERS (Search / Sort by Date / Content Type) --- */}
          <div className="flex items-center gap-4 text-gray-300 text-sm p-4 border-b border-white/10">
              
              {/* Search Bar */}
              <div className="flex items-center bg-[#151419] border border-white/20 rounded-lg px-4 py-2 flex-1 max-w-md">
                <FaSearch className="text-gray-500 mr-3" />
                <input 
                  type="text" 
                  placeholder="Search by name, email, query type..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-transparent flex-1 text-gray-200 placeholder-gray-500 focus:outline-none text-sm" 
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-gray-500 hover:text-white ml-2 transition-colors"
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
              
              {/* Sort by Date Filter */}
              <div className="relative">
                  <button 
                      onClick={() => setShowCalendar(!showCalendar)}
                      className="flex items-center gap-2 cursor-pointer hover:text-white transition-colors select-none "
                      style={{ fontFamily: "'Neue Montreal', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
                  >
                      <FaCalendarAlt className="w-4 h-4 text-gray-400" />
                      {dateFilterStart && dateFilterEnd 
                        ? `Date: ${new Date(dateFilterStart).toLocaleDateString()} - ${new Date(dateFilterEnd).toLocaleDateString()}`
                        : dateSortOrder === 'asc' ? 'Date: Oldest First' 
                        : dateSortOrder === 'desc' ? 'Date: Newest First' 
                        : 'Sort by Date'}
                      <FaChevronDown className="w-3 h-3 text-gray-400" />
                  </button>
                  {/* Sort/Filter Options Dropdown */}
                  {showCalendar && (
                    <div className="absolute top-full left-0 mt-2 z-30 w-64 bg-[#1D1C21] border border-white/20 rounded-lg shadow-xl">
                      <div className="p-2 border-b border-white/10">
                        <p className="text-xs text-gray-400 px-2 mb-2">Sort Options</p>
                        <button
                          onClick={() => {
                            setDateSortOrder('desc');
                            setShowCalendar(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-white hover:bg-white/10 transition-colors rounded"
                        >
                          Newest First
                        </button>
                        <button
                          onClick={() => {
                            setDateSortOrder('asc');
                            setShowCalendar(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-white hover:bg-white/10 transition-colors rounded"
                        >
                          Oldest First
                        </button>
                      </div>
                      <div className="p-2 border-b border-white/10">
                        <p className="text-xs text-gray-400 px-2 mb-2">Date Filter</p>
                        <input
                          type="date"
                          value={dateFilterStart ? new Date(dateFilterStart).toISOString().split('T')[0] : ''}
                          onChange={(e) => setDateFilterStart(e.target.value ? new Date(e.target.value) : null)}
                          className="w-full px-3 py-2 text-sm bg-[#151419] border border-white/20 rounded text-white mb-2"
                          placeholder="Start Date"
                        />
                        <input
                          type="date"
                          value={dateFilterEnd ? new Date(dateFilterEnd).toISOString().split('T')[0] : ''}
                          onChange={(e) => setDateFilterEnd(e.target.value ? new Date(e.target.value) : null)}
                          className="w-full px-3 py-2 text-sm bg-[#151419] border border-white/20 rounded text-white"
                          placeholder="End Date"
                        />
                      </div>
                      <button
                        onClick={() => {
                          setDateSortOrder(null);
                          setDateFilterStart(null);
                          setDateFilterEnd(null);
                          setShowCalendar(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-400 hover:bg-white/10 transition-colors"
                      >
                        Clear All
                      </button>
                    </div>
                  )}
              </div>
              
              {/* Content Type Filter (Query Type) */}
              <div className="relative">
                  <button 
                      onClick={() => setShowQueryTypeFilter(!showQueryTypeFilter)}
                      className="flex items-center gap-1 cursor-pointer hover:text-white transition-colors select-none"
                  >
                      <HiDocumentText className="w-5 h-5 text-gray-400" />
                      {selectedQueryType ? formatServiceInterest(selectedQueryType) : 'Query Type'}
                      <FaChevronDown className="w-3 h-3 text-gray-400" />
                  </button>
                  {/* Query Type Filter Dropdown */}
                  {showQueryTypeFilter && (
                    <div className="absolute top-full left-0 mt-2 z-30 w-64 bg-[#1D1C21] border border-white/20 rounded-lg shadow-xl max-h-96 overflow-y-auto">
                      <button
                        onClick={() => {
                          setSelectedQueryType(null);
                          setShowQueryTypeFilter(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-400 hover:bg-white/10 transition-colors border-b border-white/10"
                      >
                        All Query Types
                      </button>
                      {Object.entries(SERVICE_INTEREST_LABELS).map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => {
                            setSelectedQueryType(key);
                            setShowQueryTypeFilter(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                            selectedQueryType === key 
                              ? 'bg-[#E68A40]/20 text-[#E68A40]' 
                              : 'text-white hover:bg-white/10'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
              </div>
              
              {/* Clear Filters Button */}
              {(selectedQueryType || dateSortOrder || dateFilterStart || dateFilterEnd || searchQuery) && (
                <button
                  onClick={() => {
                    setSelectedQueryType(null);
                    setDateSortOrder(null);
                    setDateFilterStart(null);
                    setDateFilterEnd(null);
                    setSearchQuery('');
                  }}
                  className="ml-auto text-xs text-gray-400 hover:text-white transition-colors"
                >
                  Clear Filters
                </button>
              )}
              
              {/* Bulk Download Button */}
              {selectedRows.length > 0 && (
                <>
                  <button
                    onClick={handleBulkDownload}
                    disabled={isBulkDownloading}
                    className={`ml-4 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                      isBulkDownloading
                        ? 'bg-[#5BE4A4]/50 text-white cursor-not-allowed opacity-60'
                        : 'bg-[#5BE4A4] text-[#151419] hover:bg-[#5BE4A4]/90 hover:shadow-[0_0_25px_rgba(91,228,164,0.5)]'
                    }`}
                    title={`Download ${selectedRows.length} selected inquiry PDFs as ZIP`}
                  >
                    <FaDownload className={isBulkDownloading ? 'animate-pulse' : ''} />
                    {isBulkDownloading ? 'Downloading...' : `Download ${selectedRows.length} as ZIP`}
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={isBulkDeleting}
                    className={`ml-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                      isBulkDeleting
                        ? 'bg-red-500/50 text-white cursor-not-allowed opacity-60'
                        : 'bg-red-500/90 text-white hover:bg-red-500 hover:shadow-[0_0_25px_rgba(239,68,68,0.4)]'
                    }`}
                    title={`Remove ${selectedRows.length} selected inquiries (archived)`}
                  >
                    <FaTrashAlt className={isBulkDeleting ? 'animate-pulse' : ''} />
                    {isBulkDeleting ? 'Removing...' : `Remove ${selectedRows.length}`}
                  </button>
                </>
              )}
          </div>
          {/* --- END TOP FILTERS --- */}

          {actionBanner && (
            <div
              className={`mx-4 mt-4 rounded-lg px-4 py-3 text-sm ${
                actionBanner.type === 'error'
                  ? 'bg-red-500/15 text-red-300 border border-red-500/40'
                  : 'bg-green-500/15 text-green-200 border border-green-500/40'
              }`}
            >
              {actionBanner.message}
            </div>
          )}

          <table className="min-w-full divide-y divide-white/10">
            {/* Table Header */}
            <thead className="bg-transparent">
              <tr>
                <th className="pl-2 pr-2 py-4 text-center text-xs font-semibold text-white uppercase tracking-wider select-none cursor-default">
                  <div className="flex items-center justify-center gap-2">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={allRowsSelected}
                        onChange={toggleSelectAll}
                        className="h-5 w-5 mt-0.1 appearance-none rounded border-2 border-white 
                        bg-transparent checked:bg-white checked:border-white 
                        focus:outline-none focus:ring-2 focus:ring-[#E68A40]"
                        style={{ borderRadius: '4px' }}
                      />
                      {allRowsSelected && (
                        <FaCheck className="absolute top-[45%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 text-[#151419] pointer-events-none" />
                      )}
                    </div>
                    <span className="text-white font-normal normal-case">Select All</span>
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider select-none cursor-default">
                  Create
                </th>
                {['Name', 'Email', 'Query Type', 'Status', 'Action'].map((header) => (
                  <th 
                    key={header} 
                    scope="col" 
                    className={`px-6 py-4 text-xs font-semibold text-white uppercase tracking-wider select-none cursor-default
                                ${header === 'Action' ? 'text-center' : 'text-left'}`}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            
            {/* Table Body */}
            <tbody className="divide-y divide-white/10">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-300">
                    Loading inquiries...
                  </td>
                </tr>
              ) : fetchError ? (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-sm text-red-400">
                    {fetchError}
                    <button
                      type="button"
                      onClick={handleRetryFetch}
                      className="ml-3 text-[#E68A40] hover:text-white underline underline-offset-4"
                    >
                      Retry
                    </button>
                  </td>
                </tr>
              ) : inquiriesData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-400">
                    No inquiries yet.
                  </td>
                </tr>
              ) : (
                inquiriesData.map((inquiry, index) => {
                const displayStatus = formatStatus(inquiry.status);
                const isEditingRow = editingStatusId === inquiry._id;
                const isSavingStatus = statusUpdatingId === inquiry._id;
                const isDeleting = deletingId === inquiry._id;
                const isDownloading = downloadingId === inquiry._id;
                return (
                <tr key={inquiry._id} className={index % 2 === 0 ? 'bg-[#1F1E23]' : 'bg-[#1D1C21]'}>
                  <td className="pl-2 pr-2 py-4">
                    <div className="flex items-center justify-center">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(inquiry._id)}
                          onChange={() => toggleRowSelection(inquiry._id)}
                          className="h-5 w-5 mt-1 select-none cursor-default appearance-none rounded border-2 border-white bg-transparent checked:bg-white checked:border-white focus:outline-none focus:ring-2 focus:ring-[#E68A40]"
                          style={{ borderRadius: '4px' }}
                        />
                        {selectedRows.includes(inquiry._id) && (
                          <FaCheck className="absolute top-[45%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 text-[#151419] pointer-events-none" />
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white select-none cursor-default">
                    {formatDate(inquiry.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-medium select-none cursor-default">{inquiry.fullName}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white select-none cursor-default max-w-xs truncate" style={{ maxWidth: '250px' }}>
                    {inquiry.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white select-none cursor-default">
                    {formatServiceInterest(inquiry.serviceInterest)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm select-none cursor-default">
                    {isEditingRow ? (
                       <select
                         value={displayStatus}
                         onChange={(e) => handleStatusChange(inquiry._id, e.target.value)}
                         disabled={isSavingStatus}
                         className="bg-[#1D1C21] border border-white/30 text-white text-sm rounded-lg px-3 py-2 focus:outline-none 
                         focus:border-[#E68A40] focus:ring-1 focus:ring-[#E68A40]"
                       >
                         {statusOptions.map((status) => (
                           <option
                             key={status}
                             value={status}
                             className="bg-[#1D1C21] text-white"
                             style={{ padding: '8px 4px', marginBottom: '6px', marginTop: '6px' }}
                           >
                             {status}
                           </option>
                         ))}
                       </select>
                    ) : (
                      getStatusBadge(displayStatus)
                    )}
                  </td>
                  
                  {/* FINAL FIX: Used 'flex justify-center items-center' directly on the TD */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex justify-center items-center gap-x-3">
                      <button
                        type="button"
                        onClick={() => handleViewInquiry(inquiry)}
                        className="w-10 h-10 flex items-center justify-center rounded-xl text-[#9EA0B7] transition-all duration-200 hover:text-white 
                          hover:shadow-[0_0_25px_rgba(158,160,183,0.7)] hover:bg-[#9EA0B7] hover:bg-opacity-75"
                        style={{ border: 'none' }}
                        title="View details"
                      >
                        <MdVisibility />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownloadPdf(inquiry._id)}
                        disabled={isDownloading}
                        className={`w-10 h-10 flex items-center justify-center rounded-xl text-[#5BE4A4] transition-all duration-200 hover:text-white 
                          hover:shadow-[0_0_25px_rgba(91,228,164,0.7)] hover:bg-[#5BE4A4] hover:bg-opacity-75 ${
                            isDownloading ? 'opacity-60 cursor-not-allowed' : ''
                          }`}
                        style={{ border: 'none' }}
                        title="Download PDF"
                      >
                        <FaDownload className={isDownloading ? 'animate-pulse' : ''} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStatusEditToggle(inquiry._id)}
                        disabled={isSavingStatus}
                        className={`w-10 h-10 flex items-center justify-center rounded-xl text-[#E68A40] transition-all duration-200 hover:text-white 
                          hover:shadow-[0_0_25px_rgba(230,138,64,0.7)] hover:bg-[#E68A40] hover:bg-opacity-75 ${
                          isEditingRow
                            ? 'text-white bg-[#E68A40]'
                            : 'text-[#E68A40] bg-transparent'
                        } ${isSavingStatus ? 'opacity-60 cursor-not-allowed' : ''}`}
                        style={{ border: 'none' }}
                        title={isEditingRow ? 'Close status editor' : 'Edit status'}
                      >
                        {isSavingStatus ? (
                          <span className="text-xs font-semibold uppercase tracking-wide">...</span>
                        ) : (
                          <FaEdit />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteInquiry(inquiry._id)}
                        disabled={isDeleting}
                        className={`w-10 h-10 flex items-center justify-center rounded-xl text-[#FF6B6B] transition-all duration-200 hover:text-white 
                        hover:shadow-[0_0_25px_rgba(255,107,107,0.7)] hover:bg-[#FF6B6B] hover:bg-opacity-75 ${
                          isDeleting ? 'opacity-60 cursor-not-allowed' : ''
                        }`}
                        style={{ border: 'none' }}
                        title="Delete inquiry"
                      >
                        {isDeleting ? (
                          <span className="text-xs font-semibold uppercase tracking-wide">...</span>
                        ) : (
                          <FaTrashAlt />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              );
              })
              )}
            </tbody>
          </table>
          
          {/* --- PAGINATION SECTION (spacing adjusted to match mockup) --- */}
          <div className="flex justify-center items-center py-4 px-4 border-t border-white/10 text-sm text-gray-300 space-x-4">
              
              {/* Left Side: Prev 10 at Prev 1 */}
               <div className="flex items-center space-x-3">
                   <button
                       className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white disabled:opacity-30 disabled:cursor-not-allowed"
                       onClick={() => handlePageChange(1)}
                       disabled={!hasFirst}
                       title="First page"
                   >
                       <FaAngleDoubleLeft className="w-3 h-3" />
                   </button>
                   <button
                       className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white disabled:opacity-30 disabled:cursor-not-allowed"
                       onClick={() => handlePageChange(page - 1)}
                       disabled={!hasPrev}
                       title="Previous page"
                   >
                       <FaChevronLeft className="w-3 h-3" />
                   </button>
               </div>

              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                  {pageNumbers.map((pageNumber, i) => (
                  <button
                          key={i}
                          className={`w-8 h-8 rounded-full flex items-center justify-center font-medium select-none
                              ${pageNumber === page ? 'bg-white/20 text-white cursor-default' : (pageNumber !== '...' ? 'hover:bg-white/10 cursor-pointer text-white' : 'cursor-default text-gray-500')}`}
                          disabled={pageNumber === '...'}
                          onClick={() => typeof pageNumber === 'number' && handlePageChange(pageNumber)}
                      >
                          {pageNumber}
                      </button>
                  ))}
              </div>

              {/* Right Side: Next 1 at Next 10 */}
               <div className="flex items-center space-x-3">
                   <button
                       className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white disabled:opacity-30 disabled:cursor-not-allowed"
                       onClick={() => handlePageChange(page + 1)}
                       disabled={!hasNext}
                       title="Next page"
                   >
                       <FaChevronRight className="w-3 h-3" />
                   </button>
                   <button
                       className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white disabled:opacity-30 disabled:cursor-not-allowed"
                       onClick={() => handlePageChange(totalPages)}
                       disabled={!hasLast}
                       title="Last page"
                   >
                       <FaAngleDoubleRight className="w-3 h-3" />
                   </button>
               </div>
          </div>
          {/* --- END PAGINATION SECTION --- */}

        </div>
      </div>
    </div>

      {viewingInquiry && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-[#1D1C21] border border-white/10 rounded-2xl shadow-[0_25px_80px_rgba(0,0,0,0.65)] max-w-2xl w-full p-8 relative">
            <button
              type="button"
              onClick={closeViewModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              ✕
            </button>
            <h3 className="text-2xl font-semibold text-white mb-4">Inquiry Details</h3>
            <div className="space-y-4 text-gray-200">
              <div>
                <p className="text-sm text-gray-400">Full Name</p>
                <p className="text-lg">{viewingInquiry.fullName}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Email</p>
                  <p className="text-base break-all text-white">{viewingInquiry.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Service Interest</p>
                  <p className="text-base">{formatServiceInterest(viewingInquiry.serviceInterest)}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-400">Description</p>
                <div className="bg-[#151419] border border-white/5 rounded-xl p-4 text-sm leading-relaxed text-gray-200 whitespace-pre-line max-h-64 overflow-y-auto">
                  {viewingInquiry.description || 'No description provided.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDashboard;